'use client';

import type { ReactNode } from 'react';
import {
  CheckmarkCircle01Icon,
  InformationCircleIcon,
  Alert01Icon,
  Cancel01Icon,
  type TerraIcon,
} from '@terra/ui-icons';
import { IconButton } from './icon-button';
import { alertBanner } from './styled/alert-banner.recipe';
import { BodyDefault, BodyStrong } from './typography';

export type AlertBannerKind =
  | 'default'
  | 'success'
  | 'error'
  | 'info'
  | 'attention';
export type AlertBannerType = 'outline' | 'evaluation';
export type AlertBannerProps = {
  kind?: AlertBannerKind;
  type?: AlertBannerType;
  title?: string;
  description: ReactNode;
  action?: ReactNode;
} & (
  | {
      onDismiss?: undefined;
      dismissAriaLabel?: undefined;
    }
  | {
      onDismiss: () => void;
      dismissAriaLabel: string;
    }
);
const ALERT_BANNER_ICONS: Record<AlertBannerKind, TerraIcon> = {
  default: InformationCircleIcon,
  success: CheckmarkCircle01Icon,
  error: Alert01Icon,
  info: InformationCircleIcon,
  attention: Alert01Icon,
};

export function AlertBanner({
  kind = 'default',
  type = 'evaluation',
  title,
  description,
  action,
  onDismiss,
  dismissAriaLabel,
}: AlertBannerProps) {
  const KindIcon = ALERT_BANNER_ICONS[kind];
  const descriptionOnly = title === undefined;
  const classes = alertBanner({
    kind,
    type,
    ...(descriptionOnly ? { descriptionOnly: true } : {}),
  });

  return (
    <div className={classes.root}>
      <span className={classes.icon}>
        <KindIcon size={20} />
      </span>
      <div className={classes.content}>
        {title ? (
          <>
            <BodyStrong className={classes.title}>{title}</BodyStrong>
            <BodyDefault className={classes.description}>
              {description}
            </BodyDefault>
          </>
        ) : (
          <BodyDefault className={classes.description}>
            {description}
          </BodyDefault>
        )}
      </div>
      {action ? <div className={classes.actions}>{action}</div> : null}
      {onDismiss ? (
        <IconButton
          variant="ghost"
          size="xs"
          aria-label={dismissAriaLabel}
          onClick={onDismiss}
        >
          <Cancel01Icon size={16} />
        </IconButton>
      ) : null}
    </div>
  );
}

AlertBanner.displayName = 'AlertBanner';
